import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { 
  getAuth, 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail
} from 'firebase/auth';
import { app, analytics } from '../firebaseConfig';
import { isCodexUi, isCodexUnauthenticated } from '../utils/codexEnv';
import { useDispatch } from 'react-redux';
import { setCurrentUser, setNeedsVerification, clearUserInfo } from '../redux/userSlice';
import { logEvent } from 'firebase/analytics';

const FirebaseContext = createContext();

export const useFirebase = () => {
  return useContext(FirebaseContext);
};

export const FirebaseProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const auth = getAuth(app);
  const googleProvider = new GoogleAuthProvider();
  const codexListenersRef = useRef([]);

  const codexUser = {
    uid: 'codex-user',
    email: 'codex@local.dev',
    emailVerified: true,
    displayName: 'Codex Trader',
    photoURL: null,
    providerData: [{ providerId: 'codex' }],
    getIdToken: async () => 'codex-test-token',
  };
  const [codexCurrentUser, setCodexCurrentUser] = useState(
    isCodexUnauthenticated ? null : codexUser
  );

  useEffect(() => {
    if (isCodexUi) {
      if (!codexCurrentUser) {
        dispatch(clearUserInfo());
      } else {
        dispatch(setCurrentUser({
          uid: codexCurrentUser.uid,
          email: codexCurrentUser.email,
          emailVerified: codexCurrentUser.emailVerified,
          displayName: codexCurrentUser.displayName,
          photoURL: codexCurrentUser.photoURL,
        }));
        dispatch(setNeedsVerification(false));
      }
      setLoading(false);
      return () => {};
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        dispatch(setCurrentUser({
          uid: user.uid,
          email: user.email,
          emailVerified: user.emailVerified,
          displayName: user.displayName,
          photoURL: user.photoURL
        }));
        dispatch(setNeedsVerification(!user.emailVerified));
        
        // Log sign in event
        if (!isCodexUi && analytics) {
          logEvent(analytics, 'login', {
            method: user.providerData[0]?.providerId || 'email',
            email_verified: user.emailVerified
          });
        }
      } else {
        dispatch(clearUserInfo());
        // Log sign out event
        if (!isCodexUi && analytics) {
          logEvent(analytics, 'logout');
        }
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [dispatch, auth, codexCurrentUser]);

  useEffect(() => {
    if (!isCodexUi) {
      return;
    }
    codexListenersRef.current.forEach((listener) => {
      listener(codexCurrentUser);
    });
  }, [codexCurrentUser]);

  const signUpWithEmail = async (email, password) => {
    if (isCodexUi) {
      setCodexCurrentUser(codexUser);
      return { user: codexUser };
    }
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await sendEmailVerification(result.user);
    
    // Log sign up event
    if (!isCodexUi && analytics) {
      logEvent(analytics, 'sign_up', {
        method: 'email'
      });
    }
    
    return result;
  };

  const signInWithEmail = async (email, password) => {
    if (isCodexUi) {
      setCodexCurrentUser(codexUser);
      dispatch(setCurrentUser({
        uid: codexUser.uid,
        email: codexUser.email,
        emailVerified: codexUser.emailVerified,
        displayName: codexUser.displayName,
        photoURL: codexUser.photoURL,
      }));
      dispatch(setNeedsVerification(false));
      return { user: codexUser };
    }
    const result = await signInWithEmailAndPassword(auth, email, password);
    
    // Log sign in event
    if (!isCodexUi && analytics) {
      logEvent(analytics, 'login', {
        method: 'email'
      });
    }
    
    return result;
  };

  const signInWithGoogle = async () => {
    if (isCodexUi) {
      setCodexCurrentUser(codexUser);
      dispatch(setCurrentUser({
        uid: codexUser.uid,
        email: codexUser.email,
        emailVerified: codexUser.emailVerified,
        displayName: codexUser.displayName,
        photoURL: codexUser.photoURL,
      }));
      dispatch(setNeedsVerification(false));
      return { user: codexUser };
    }
    const result = await signInWithPopup(auth, googleProvider);
    
    // Log sign in event
    if (!isCodexUi && analytics) {
      logEvent(analytics, 'login', {
        method: 'google'
      });
    }
    
    return result;
  };

  const logOut = async () => {
    if (isCodexUi) {
      setCodexCurrentUser(null);
      dispatch(clearUserInfo());
      return;
    }
    await signOut(auth);
    
    // Log sign out event
    if (!isCodexUi && analytics) {
      logEvent(analytics, 'logout');
    }
  };

  const resetPassword = async (email) => {
    if (isCodexUi) {
      return;
    }
    await sendPasswordResetEmail(auth, email);
    
    // Log password reset event
    if (!isCodexUi && analytics) {
      logEvent(analytics, 'password_reset');
    }
  };

  const resendVerificationEmail = async () => {
    if (isCodexUi) {
      return;
    }
    if (auth.currentUser) {
      await sendEmailVerification(auth.currentUser);
      
      // Log email verification resend event
      if (!isCodexUi && analytics) {
        logEvent(analytics, 'verification_email_resent', {
          email: auth.currentUser.email
        });
      }
    } else {
      throw new Error('No user is currently signed in');
    }
  };

  const value = {
    auth: isCodexUi
      ? {
          currentUser: codexCurrentUser,
          onAuthStateChanged: (callback) => {
            codexListenersRef.current.push(callback);
            callback(codexCurrentUser);
            return () => {
              codexListenersRef.current = codexListenersRef.current.filter(
                (listener) => listener !== callback
              );
            };
          },
        }
      : auth,
    signUpWithEmail,
    signInWithEmail,
    signInWithGoogle,
    logOut,
    resetPassword,
    resendVerificationEmail,
    currentUser: isCodexUi ? codexCurrentUser : auth.currentUser
  };

  return (
    <FirebaseContext.Provider value={value}>
      {!loading && children}
    </FirebaseContext.Provider>
  );
}; 
